import React from "react";
import LocalFilterPanel from "../../../components/common/LocalFilterPanel";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";

const AttributeTableSettings = ({
  mainAttribute,
  onMainAttributeChange,
  additionalAttributes,
  onAdditionalAttributesChange,
  componentTitle,
  onTitleChange,
  hideEmptyValues,
  onHideEmptyValuesChange,
  objectAttributes,
  loading = false,
  selectionMode,
  onActivateSelectionMode,
  onCancelSelectionMode,
  filterMode,
  selectionLabel,
  selectedObjectIds,
  onClearSelection,
  selectedEdgeInfo,
  localFilters,
  updateLocalFilter,
  activeFile,
  onAddEdgeDurationFilter,
  onAddEdgeContainmentFilter,
}) => {
  return (
    <div className="space-y-6">
      {/* Component Title Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Component Title (optional)
        </label>
        <input
          type="text"
          value={componentTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Attribute Table"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-1">
          Custom title displayed in the component header
        </p>
      </div>

      {/* Main Attribute Dropdown (Mandatory) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Main Attribute <span className="text-red-500">*</span>
        </label>
        <select
          value={mainAttribute}
          onChange={(e) => onMainAttributeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={loading || objectAttributes.length === 0}
        >
          {objectAttributes.length === 0 ? (
            <option value="">No attributes available</option>
          ) : (
            <>
              <option value="">Select main attribute...</option>
              {objectAttributes.map((attr) => (
                <option key={attr} value={attr}>
                  {attr}
                </option>
              ))}
            </>
          )}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Defines the row basis (first column)
        </p>
        {!mainAttribute && (
          <p className="text-xs text-red-600 mt-1">
            ⚠️ Main attribute is required
          </p>
        )}
      </div>

      {/* Additional Attributes Multi-Select (Optional) */}
      <div>
        <MultiSelectDropdown
          label="Additional Attributes (optional)"
          options={objectAttributes.filter((attr) => attr !== mainAttribute)}
          selectedValues={additionalAttributes}
          onSelectionChange={onAdditionalAttributesChange}
          placeholder="Select additional attributes..."
          disabled={loading || objectAttributes.length === 0}
          itemType="attributes"
        />
        <p className="text-xs text-gray-500 mt-1">
          Select attributes to display as additional columns
        </p>
      </div>

      {/* Hide Empty Values Toggle */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hideEmptyValues}
            onChange={(e) => onHideEmptyValuesChange(e.target.checked)}
            disabled={loading}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Leere Werte ausblenden
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          Prozentwerte werden dann nur auf Basis der angezeigten (nicht-leeren) Werte berechnet.
        </p>
      </div>

      {/* Graph Selection Button */}
      {!selectionMode && !selectedEdgeInfo && (
        <div>
          <button
            onClick={onActivateSelectionMode}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
          >
            Select Edge/Node from Graph
          </button>
        </div>
      )}

      {/* Active Selection Mode Indicator */}
      {selectionMode && (
        <div className="p-3 bg-blue-50 border-2 border-blue-300 rounded-md animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                />
              </svg>
              <p className="text-sm font-semibold text-blue-800">
                Click an edge or node on the graph...
              </p>
            </div>
            <button
              onClick={onCancelSelectionMode}
              className="px-3 py-1 text-xs bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-100 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edge/Node Mode Indicator */}
      {filterMode && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-800">
                  {filterMode === "edge"
                    ? "Edge Mode Active"
                    : "Node Mode Active"}
                </p>
                <p className="text-xs text-blue-600 mt-0.5">
                  {selectionLabel} • {selectedObjectIds.length} objects
                </p>
              </div>
            </div>
            <button
              onClick={onClearSelection}
              className="px-3 py-1 text-xs bg-white border border-blue-300 text-red-700 rounded hover:bg-blue-100 transition-colors"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Local Filter Panel */}
      <LocalFilterPanel
        localFilters={localFilters}
        updateLocalFilter={updateLocalFilter}
        activeFile={activeFile}
        disabled={loading}
        onAddEdgeDuration={onAddEdgeDurationFilter}
        onAddEdgeContainment={onAddEdgeContainmentFilter}
      />
    </div>
  );
};

export default AttributeTableSettings;
